import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaLock, FaSignInAlt, FaEye, FaEyeSlash, FaSignOutAlt, FaTrash, FaDownload, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './Admin.css';

// Dynamic API URL: Use environment variable or derive from current domain
const API_BASE = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'http://localhost:5001' 
    : `https://safarnamah-backend-lc7p.onrender.com`);

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // Fixed page size
  const [totalTrips, setTotalTrips] = useState(0);
  const [token, setToken] = useState('');
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');

  // Clear token on page load/refresh
  useEffect(() => {
    localStorage.removeItem('adminToken');
  }, []);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      fetchTrips(token);
    }
    // fetchTrips is defined below and stable for this effect's purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Inactivity logout - 5 minutes (300000 ms)
  useEffect(() => {
    if (!isAuthenticated) return;

    let inactivityTimer;

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        handleLogout();
        alert('Session expired due to inactivity. Please log in again.');
      }, 5 * 60 * 1000); // 5 minutes
    };

    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    // Set initial timer
    resetInactivityTimer();

    // Add event listeners for user activity
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    // Cleanup
    return () => {
      clearTimeout(inactivityTimer);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, [isAuthenticated]);

  const fetchTrips = async (authToken) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/trips?page=${currentPage}&pageSize=${pageSize}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const data = await response.json();
      if (data.items) {
        setTrips(data.items);
        setTotalTrips(data.total || data.items.length);
      } else if (data.trips) {
        setTrips(data.trips);
        setTotalTrips(data.total || data.trips.length);
      }
    } catch (err) {
      console.error('Error fetching trips:', err);
      setError('Failed to fetch trips');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (data.token) {
        setToken(data.token);
        localStorage.setItem('adminToken', data.token);
        setIsAuthenticated(true);
        setCredentials({ username: '', password: '' });
      } else {
        setError(data.message || 'Invalid credentials');
      }
    } catch (error) {
      setError('Unable to connect to server. Please try again.');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setToken('');
    setCredentials({ username: '', password: '' });
    setTrips([]);
  };

  const handleDeleteTrip = async (tripId) => {
    if (window.confirm('Are you sure you want to delete this trip request?')) {
      try {
        await fetch(`${API_BASE}/api/admin/trips/${tripId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchTrips(token);
        setExpandedTrip(null);
      } catch (err) {
        console.error('Error deleting trip:', err);
        setError('Failed to delete trip');
      }
    }
  };

  const handleDownloadCSV = () => {
    if (trips.length === 0) return;

    const headers = ['Name', 'Email', 'Phone', 'Destination', 'Start Date', 'End Date', 'Budget', 'Travelers', 'Interests'];
    const rows = trips.map(trip => [
      trip.name,
      trip.email,
      trip.phone,
      trip.destination,
      trip.startDate,
      trip.endDate,
      trip.budget,
      trip.travelers,
      trip.interests?.join(', ') || '',
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trip-requests.csv';
    a.click();
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <Helmet>
          <title>Admin Login - Safarnamah</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <motion.div
          className="admin-login-container"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="login-card">
            <div className="login-header">
              <FaLock className="lock-icon" />
              <h1>Admin Panel</h1>
              <p>Secure Access Required</p>
            </div>

            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={credentials.username}
                  onChange={handleInputChange}
                  placeholder="Enter your username"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={credentials.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {error && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.div>
              )}

              <button
                type="submit"
                className="login-btn"
                disabled={loading}
              >
                <FaSignInAlt /> {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard (authenticated)
  return (
    <div className="admin-page">
      <Helmet>
        <title>Admin Dashboard - Safarnamah</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <motion.div
        className="admin-dashboard"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="dashboard-header">
          <h1>Admin Dashboard</h1>
          <button className="logout-btn" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </div>

        <div className="dashboard-tabs">
          <button
            className={`tab-btn ${activeSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveSection('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={`tab-btn ${activeSection === 'trips' ? 'active' : ''}`}
            onClick={() => setActiveSection('trips')}
          >
            Trip Requests ({totalTrips})
          </button>
        </div>

        {activeSection === 'dashboard' && (
          <div className="dashboard-content">
            <div className="dashboard-card">
              <h2>Welcome to Admin Panel</h2>
              <p>Manage your Safarnamah travel content and trip requests.</p>
            </div>

            <div className="dashboard-card">
              <h2>Quick Stats</h2>
              <div className="stats-grid">
                <div className="stat"><span className="label">Total Requests</span><span className="value">{totalTrips}</span></div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'trips' && (
          <div className="trips-content">
            <div className="trips-header">
              <h2>Trip Planning Requests</h2>
              <button onClick={handleDownloadCSV} className="download-btn">
                <FaDownload /> Download CSV
              </button>
            </div>

            {error && (
              <motion.div className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {error}
              </motion.div>
            )}

            {loading ? (
              <div className="loading">Loading trips...</div>
            ) : trips.length > 0 ? (
              <div className="trips-list">
                {trips.map((trip) => (
                  <motion.div
                    key={trip.id}
                    className="trip-card"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div
                      className="trip-header"
                      onClick={() => setExpandedTrip(expandedTrip === trip.id ? null : trip.id)}
                    >
                      <div className="trip-info">
                        <h3>{trip.name}</h3>
                        <p>{trip.email} | {trip.phone}</p>
                      </div>
                      <div className="trip-toggle">
                        {expandedTrip === trip.id ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    </div>

                    {expandedTrip === trip.id && (
                      <motion.div
                        className="trip-details"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="detail-group">
                          <label>Destination:</label>
                          <p>{trip.destination}</p>
                        </div>

                        <div className="detail-row">
                          <div className="detail-group">
                            <label>Start Date:</label>
                            <p>{trip.startDate || 'Not specified'}</p>
                          </div>
                          <div className="detail-group">
                            <label>End Date:</label>
                            <p>{trip.endDate || 'Not specified'}</p>
                          </div>
                        </div>

                        <div className="detail-row">
                          <div className="detail-group">
                            <label>Budget:</label>
                            <p>{trip.budget || 'Not specified'}</p>
                          </div>
                          <div className="detail-group">
                            <label>Travelers:</label>
                            <p>{trip.travelers || '1'}</p>
                          </div>
                        </div>

                        <div className="detail-group">
                          <label>Travel With:</label>
                          <p>{trip.travelWith || 'Not specified'}</p>
                        </div>

                        <div className="detail-group">
                          <label>Interests:</label>
                          <p>{trip.interests?.join(', ') || 'Not specified'}</p>
                        </div>

                        <div className="detail-group">
                          <label>Accommodation:</label>
                          <p>{trip.accommodation || 'Not specified'}</p>
                        </div>

                        <div className="detail-group">
                          <label>Special Requirements:</label>
                          <p>{trip.specialRequirements || 'None'}</p>
                        </div>

                        <div className="trip-actions">
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteTrip(trip.id)}
                          >
                            <FaTrash /> Delete
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="no-trips">
                <p>No trip requests found</p>
              </div>
            )}

            <div className="pagination">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {Math.ceil(totalTrips / pageSize)}
              </span>
              <button
                onClick={() =>
                  setCurrentPage(
                    Math.min(
                      Math.ceil(totalTrips / pageSize),
                      currentPage + 1
                    )
                  )
                }
                disabled={currentPage >= Math.ceil(totalTrips / pageSize)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Admin;
